import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UploadMultipleFilesModalComponent } from '@components/upload-multiple-files-modal/upload-multiple-files-modal.component';
import { UploadMultipleFilesWithContentModalComponent } from '@components/upload-multiple-files-with-content-modal/upload-multiple-files-with-content-modal.component';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { ChannelTypeEnum } from 'src/app/enums/channel-type.enum';
import { TestTypes } from 'src/app/enums/test-coupon-enums';
import { showNotification } from 'src/app/helpers/show-toast';
import { IFile } from 'src/app/_interfaces/files/i-files';
import { IMenuElement } from 'src/app/_interfaces/menu-elements/i-menu-elements';
import { IPatientEmrDetails } from 'src/app/_interfaces/patients/i-patient-emr-details';
import { EhrPrescriptionFilesServiceService } from 'src/app/_services/patient-emr/ehr-prescription-files-service.service';
import { EhrTestResultsFilesServiceService } from 'src/app/_services/patient-emr/ehr-test-results-files-service.service';
import { PatientEmrDetailsService } from 'src/app/_services/patient-emr/patient-emr-details.service';
import { PrescriptionService } from 'src/app/_services/prescription-fulfillment/prescription.service';

@Component({
  selector: 'patient-emr',
  templateUrl: './patient-emr.component.html',
  styleUrls: ['./patient-emr.component.scss'],
})
export class PatientEmrComponent implements OnInit {
  patientId: string = '';
  patientEmrDetails: IPatientEmrDetails;
  testMenuElements: IMenuElement[] = [
    { id: 1, content: 'Add Test Request', icon: 'icon-add' },
    { id: 2, content: 'Add Medical Test(s) File(s)', icon: 'icon-add-test-result' },
    { id: 3, content: 'View All', icon: 'icon-view-files' }
  ]
  prescriptionMenuElements: IMenuElement[] = [
    { id: 1, content: 'Add Prescription File(s)', icon: 'icon-add-prescription' },
    { id: 2, content: 'View All', icon: 'icon-view-files' }
  ]
  modalRef: BsModalRef;

  constructor(
    private activatedRoute: ActivatedRoute,
    private patientEmrDetailsService: PatientEmrDetailsService,
    private router: Router,
    private modalService: BsModalService,
    private ehrTestResultsFilesServiceService: EhrTestResultsFilesServiceService,
    private ehrPrescriptionilesServiceService: EhrPrescriptionFilesServiceService,
    private toastr: ToastrService,
    private datePipe: DatePipe,
    private prescriptionService: PrescriptionService,
  ) {
    this.patientId = this.activatedRoute.snapshot.params.patientId;
  }

  ngOnInit(): void {
    this.getPatientEmrDetails(this.patientId);
  }

  getPatientEmrDetails(patientId) {
    this.patientEmrDetailsService
      .getPatientEhrDetails(patientId)
      .subscribe((response) => {
        this.patientEmrDetails = response.data;
      });
  }

  getChannelType(channelId) {
    if (channelId == ChannelTypeEnum.application) return 'Application';
    else if (channelId == ChannelTypeEnum.admin) return 'Admin';
    else if (channelId == ChannelTypeEnum.portal) return 'Portal';
    return '--';
  }

  formatDate(dateValue: any): string {
    if (!dateValue) return '--';

    // Check if the value is a valid date
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) {
      // If it's not a valid date, return the original value or a fallback
      return typeof dateValue === 'string' ? dateValue : '--';
    }

    // Use DatePipe to format the valid date
    return this.datePipe.transform(date, 'mediumDate') || '--';
  }

  isValidDate(dateValue: any): boolean {
    if (!dateValue) return false;
    const date = new Date(dateValue);
    return !isNaN(date.getTime());
  }

  getValidTestDate(type: string, event: any): string | null {
    if (type === 'test') {
      return this.datePipe.transform(event.testDate, "yyyy-MM-dd");
    } else {
      // For non-test types, only transform if event.title is a valid date
      return this.isValidDate(event.title) ? this.datePipe.transform(event.title, "yyyy-MM-dd") : null;
    }
  }

  onTestItemSelected(event) {
    if (event.id === 1) {
      this.router.navigate(['patient-details', this.patientId, 'add-new-test']);
    }
    else if (event.id === 2) {
      this.openModal('test');
    }
    else if (event.id === 3) {
      this.navigateToViewAllMedicalTests();
    }
  }


  navigateToViewAllMedicalTests() {
    this.router.navigate(['medical-tests'], { relativeTo: this.activatedRoute });
  }

  navigateToViewAllHospitalProcedures() {
    this.router.navigate(['hospital-Procedures'], { relativeTo: this.activatedRoute });
  }

  navigateToViewAllPrescriptions() {
    this.router.navigate(['medications'], { relativeTo: this.activatedRoute });
  }

  openModal(source: string) {
    const initialState: ModalOptions<any> = {
      keyboard: true,
      class: 'modal-dialog-centered',
      initialState: {
        title: 'Upload Files',
        subtitle: 'Please add date for this file then upload files',
        isHaveContent: true,
      }
    };

    if (source == 'test') {
      initialState.initialState["recentMedicalTests"] = true;
      initialState.initialState["isHaveSelect"] = "true";
      initialState.initialState["selectTitle"] = "Test Type";
      initialState.initialState["selectFirstOptionValue"] = "Select Test Type";
      initialState.initialState["selectOptions"] = [
        { key: TestTypes.Lab, value: "Lab Test" },
        { key: TestTypes.Scan, value: "Radiology" },
      ];
    }

    this.modalRef = this.modalService.show(
      UploadMultipleFilesWithContentModalComponent,
      initialState
    );

    this.modalRef.content.event.subscribe((response) => {
      if (response.action === 'uploadFiles' && source == 'test') {
        this.addEhrTestResultsFiles(this.patientId, response.testDate, response.filesToUpload,
          response.selectedOption, response.testsIds, response.doctorName);
      }
      else if (response.action === 'uploadFiles' && source == 'prescription') {
        this.addEhrPrescriptionResultsFiles(this.patientId, response.testDate, response.filesToUpload);
      }
    });
  }

  addEhrTestResultsFiles(patientId, testDate, files, testTypeId, testsIds, doctorName) {
    this.ehrTestResultsFilesServiceService.addEhrTestResultsFilesByAdmin(
      patientId, testDate, files, testTypeId, testsIds, doctorName)
      .subscribe((res) => {
        if (res.data.flag) {
          this.navigateToViewAllMedicalTests();
          showNotification('success', 'Test result is added successfully', this.toastr);
        }
        else {
          showNotification('danger', res.errorList[0].message, this.toastr);
        }
        this.modalRef.hide();
      });
  }

  addEhrPrescriptionResultsFiles(patientId, testDate, files) {
    this.ehrPrescriptionilesServiceService.addEhrPrescriptionFilesByAdmin(patientId,
      testDate, files)
      .subscribe((res) => {
        if (res.data.flag) {
          this.navigateToViewAllPrescriptions();
          showNotification('success', 'Prescription is added successfully', this.toastr);
        }
        else {
          showNotification('danger', res.errorList[0].message, this.toastr);
        }
        this.modalRef.hide();
      });
  }

  viewFiles(event, type) {

    let files: IFile[] = event.attachmentUrls?.map(a => {
      return {
        id: a.id,
        path: a.path,
        isEditable: type === 'test' ? event.isEditable : true,
      }
    })

    const initialState: ModalOptions<any> = {
      keyboard: true,
      class: 'modal-dialog-centered',
      initialState: {
        title: type == 'testPrescription' ? 'UploadResults' : 'Upload Files',
        subtitle: type == 'testPrescription' ? 'Please upload test result for this request' : 'Please add date for this file then upload files',
        filesExisted: files,
        isUploadable: true,
        isHaveContent: true,
        testDate: this.getValidTestDate(type, event),
        isUpdate: true
      }
    };

    if (type == 'testPrescription') {
      this.modalRef = this.modalService.show(
        UploadMultipleFilesModalComponent,
        initialState
      );
    } else {
      this.modalRef = this.modalService.show(
        UploadMultipleFilesWithContentModalComponent,
        initialState
      );
    }


    this.modalRef.content.event.subscribe((response) => {
      if (response.action === 'deleteFile') {
        if (type === 'test') {
          this.deleteTestResultFilesAddedByAdmin(event.id, response.file.id);
        }
        else if (type === 'prescription') {
          this.deletePrescriptionFilesAddedByAdmin(event.id, response.file.id);
        }
      }

      if (response.action === 'uploadFiles') {
        if (type === 'test') {
          this.updateEhrTestResultsFilesByAdmin(event.id, response.testDate, response.filesToUpload, event.testDate);
        }
        else if (type === 'prescription') {
          this.updateEhrPrescriptionFileByAdmin(event.id, response.testDate, response.filesToUpload, event.testDate);
        }
        else if (type === 'testPrescription') {
          this.prescriptionService.uploadTestResults(event.id, response.filesToUpload)
            .subscribe(response => {
              if (response.data.attachmentUrls) {
                showNotification('success', 'Results uploaded successfully', this.toastr);
                event.isEditable = false;
              }
              else
                showNotification('danger', response.errorList[0].message, this.toastr);
            });
        }
      }
    });
  }

  onPrescriptionItemSelected(event) {
    if (event.id === 1) {
      this.openModal('prescription');
    }
    else if (event.id === 2) {
      this.navigateToViewAllPrescriptions();
    }
  }

  deleteTestResultFilesAddedByAdmin(ehrTransactionId, fileId) {
    this.ehrTestResultsFilesServiceService.deleteTestResultFilesAddedByAdmin(ehrTransactionId, fileId)
      .subscribe((res) => {
        if (res.data.flag) {
          showNotification('success', 'file is deleted successfully', this.toastr);
        }
        else {
          showNotification('danger', res.errorList[0].message, this.toastr);
        }
      });
  }

  deletePrescriptionFilesAddedByAdmin(ehrTransactionId, fileId) {
    this.ehrPrescriptionilesServiceService.deletePrescriptionFilesAddedByAdmin(ehrTransactionId, fileId)
      .subscribe((res) => {
        if (res.data.flag) {
          showNotification('success', 'file is deleted successfully', this.toastr);
        }
        else {
          showNotification('danger', res.errorList[0].message, this.toastr);
        }
      });
  }

  updateEhrTestResultsFilesByAdmin(ehrTransactionId, testDate, files, ehrTransactionTestDate) {
    if (!files.length
      && (this.datePipe.transform(testDate, 'yyyy-MM-dd') == this.datePipe.transform(ehrTransactionTestDate, 'yyyy-MM-dd'))) {
      this.navigateToViewAllMedicalTests();
      return;
    }
    this.ehrTestResultsFilesServiceService.updateEhrTestResultsFilesByAdmin(ehrTransactionId,
      testDate, files)
      .subscribe((res) => {
        if (res.data) {
          this.navigateToViewAllMedicalTests();
          showNotification('success', 'Test result is updated successfully', this.toastr);
        }
        else {
          showNotification('danger', res.errorList[0].message, this.toastr);
        }
        this.modalRef.hide();
      });
  }

  updateEhrPrescriptionFileByAdmin(ehrTransactionId, testDate, files, ehrTransactionTestDate) {
    if (!files.length
      && (this.datePipe.transform(testDate, 'yyyy-MM-dd') == this.datePipe.transform(ehrTransactionTestDate, 'yyyy-MM-dd'))) {
      this.navigateToViewAllPrescriptions();
      return;
    }
    this.ehrPrescriptionilesServiceService.updateEhrPrescriptionFileByAdmin(ehrTransactionId,
      testDate, files)
      .subscribe((res) => {
        if (res.data) {
          this.navigateToViewAllPrescriptions();
          showNotification('success', 'Prescription is updated successfully', this.toastr);
        }
        else {
          showNotification('danger', res.errorList[0].message, this.toastr);
        }
        this.modalRef.hide();
      });
  }
}
